import { IDoctorInfo } from 'src/entity/DoctorEntity';
import { IDrugStoreInfo } from 'src/entity/DrugStoreEntity';
import DoctorService from 'src/services/DoctorService';
import I18N from 'src/services/I18NService';
import { UtilDateTime } from 'src/utils/datetime';
import * as XLSX from 'xlsx';
import { ITaskResult } from 'src/entity/TaskEntity';
import { IOrderResult } from './WalletService';
import { EnumOrderType, ORDER_STATUS } from 'src/constants/Order';
import { IOrderListColumnData } from 'src/entity/ColumnDataEntity';
import { IMallTCMDetail, IMallCPMDetail } from 'src/entity/MallEntity';
import { ISpuMedicineDetail } from 'src/entity/SpuEntity';
import { getLocationNameChain } from 'src/redux/actions/locationCreator';

interface IExcelColumnName {
  [key: string]: any;
}

interface IExcelFile {
  fileName: string;
  sheetList: IExcelSheet[];
}

interface IExcelSheet {
  sheetName: string;
  data: any;
}

export interface ITaskMedicineUpload extends ITaskResult {
  /**
   * 改价申请的uuid
   * 配药中心导入价格时，弹出改价申请窗口
   * @type {string}
   * @memberof IMedicineUploadTask
   */
  adjustment?: string;
}

class FileService {
  /**
   * 配药中心导出
   * @memberof FileService
   */
  drugStoreExport = (dataList: IDrugStoreInfo[], isDomestic: boolean = true): void => {
    const list: IExcelColumnName[] = [];
    const emailKey = 'email';
    for (const obj of dataList) {
      const dt: IExcelColumnName = {};
      dt[I18N.get('drug_store_name', false)] = obj.name;
      dt[I18N.get('common_full_address', false)] = obj.address;
      if (isDomestic) {
        dt[I18N.get('common_mobile', false)] = obj.admin_mobile;
        dt[emailKey] = obj.admin_email || '';
      } else {
        dt[emailKey] = obj.admin_email || '';
        dt[I18N.get('common_mobile', false)] = obj.admin_mobile;
      }
      dt[I18N.get('login_userName', false)] = obj.admin_username;
      dt[I18N.get('common_status', false)] = !obj.is_active ? I18N.get('common_status_stop', false) : I18N.get('common_status_start', false);
      dt[I18N.get('common_admin', false)] = obj.admin_name;
      list.push(dt);
    }

    const excelData: IExcelFile = {
      fileName: `${I18N.get('file_durg_store_info')}${UtilDateTime.nowFormat('yyyyMMddhhmmss')}.xlsx`,
      sheetList: [
        {
          sheetName: I18N.get('nav_drugStore', false),
          data: list,
        },
      ],
    };
    this.saveExcel(excelData);
  };

  /**
   * 代理商医生导出
   * @memberof FileService
   */
  doctorExport = (doctorList: IDoctorInfo[]): void => {
    const list: IExcelColumnName[] = [];
    for (const obj of doctorList) {
      const workAddress = `${getLocationNameChain(obj.location_id).join(' ')} ${obj.work_address}`;

      const dt: IExcelColumnName = {};
      dt[I18N.get('common_name', false)] = obj.last_name + obj.first_name;
      dt[I18N.get('common_mobile', false)] = obj.mobile;
      dt[I18N.get('login_userName', false)] = obj.username;
      dt[I18N.get('doc_account_type', false)] = DoctorService.getCertificationLevel(obj.certification_level);
      dt[I18N.get('doc_position', false)] = obj.position;
      dt[I18N.get('doc_area', false)] = workAddress;
      dt[I18N.get('common_status', false)] = obj.is_certificated
        ? I18N.get('common_status_authenticated', false)
        : I18N.get('common_status_unauthorized', false);
      dt[I18N.get('doc_validity_date', false)] = obj.valid_time;
      dt[I18N.get('doc_sales', false)] = obj.salesperson_name;
      dt[I18N.get('common_create_time', false)] = UtilDateTime.datetimeFormat('yyyy-MM-dd', new Date(obj.created_time));
      dt[I18N.get('doc_handling_time', false)] = UtilDateTime.datetimeFormat('yyyy-MM-dd', new Date(obj.verification_time));
      dt[I18N.get('doc_enabled', false)] = obj.is_active ? I18N.get('common_yes', false) : I18N.get('common_no', false);
      list.push(dt);
    }

    const excelData: IExcelFile = {
      fileName: `${I18N.get('file_doctor_info')}${UtilDateTime.nowFormat('yyyyMMddhhmmss')}.xlsx`,
      sheetList: [
        {
          sheetName: I18N.get('common_doctor_list', false),
          data: list,
        },
      ],
    };
    this.saveExcel(excelData);
  };

  /**
   * 订单导出
   */
  orderExport = (isAgent: boolean, status: ORDER_STATUS, dataList: IOrderListColumnData[]): void => {
    const list: IExcelColumnName[] = [];
    for (const order of dataList) {
      const dt: IExcelColumnName = {};
      dt[I18N.get('order_no', false)] = order.out_trade_no;
      dt.下发时间 = order.distribute_time || '';
      dt.收货信息 = order.receiver;
      dt.药库 = order.groupName;
      dt.打印状态 = order.printed ? '已打印' : '未打印';
      dt.关联运单 = order.expressNo;
      //   订单金额
      dt[I18N.get('order_payment_amount', false)] = order.orderPaymentAmount;
      //   分润金额
      dt[I18N.get('order_received_profit_amount', false)] = order.userReceivableProfitAmount;
      dt.医疗机构 = order.institution_name;
      list.push(dt);
    }
    const orderStatusDesc = this.getPayStatusDesc(status);
    const excelData: IExcelFile = {
      fileName: `${I18N.get('file_order_info')}(${UtilDateTime.nowFormat('yyyy-MM-dd')})(${orderStatusDesc}).xlsx`,
      sheetList: [
        {
          sheetName: I18N.get('common_order_list', false),
          data: list,
        },
      ],
    };
    this.saveExcel(excelData);
  };

  getPayStatusDesc = (status: ORDER_STATUS): string => {
    let str = '';
    switch (status) {
      case ORDER_STATUS.STATUS_PAID:
        str = I18N.get('common_status_pending');
        break;
      case ORDER_STATUS.STATUS_FINISHED:
        str = I18N.get('common_status_finish');
        break;
      case ORDER_STATUS.STATUS_SENT:
        str = I18N.get('common_status_sent');
        break;
      case ORDER_STATUS.STATUS_SIGNED:
        str = I18N.get('common_status_signed');
        break;
      case ORDER_STATUS.STATUS_OTHER:
        str = I18N.get('common_status_other');
        break;
    }
    return str;
  };

  /**
   * 交易明细导出
   * @memberof FileService
   */
  tradeOrderExport = (rangeDate: { startDate: string; endDate: string }, orderList: IOrderResult[]): void => {
    const list: IExcelColumnName[] = [];
    const createdTime = '支付时间';
    const tradeNo = '交易流水号';
    const orderNo = '订单号';
    const amountChanged = '金额';
    const amount = '账户余额';
    const note = '备注';
    for (const obj of orderList) {
      const dt: IExcelColumnName = {};
      dt[createdTime] = obj.created_time;
      dt[tradeNo] = obj.trade_no;
      dt[orderNo] = obj.order_no;
      dt[amountChanged] = obj.amount_changed;
      dt[amount] = obj.amount;
      dt[note] = obj.extend_info;
      list.push(dt);
    }

    const excelData: IExcelFile = {
      fileName: `交易明细(${rangeDate.startDate}~${rangeDate.endDate}).xlsx`,
      sheetList: [
        {
          sheetName: '交易明细',
          data: list,
        },
      ],
    };
    this.saveExcel(excelData);
  };

  /**
   * 中药导出
   *
   * @memberof FileService
   */
  // mallTcmExport = (fileName: string, mallMedicineList: IMallTCMDetail[], spuMedicineList: ISpuMedicineDetail[], orderType: EnumOrderType): void => {
  //   // 中药
  //   const tcmSheet = (): IExcelSheet => {
  //     const dataList: IExcelColumnName[] = [];

  //     const dtRemark: IExcelColumnName = {};
  //     // 脉景品名
  //     dtRemark[I18N.get('med_spu_name', false)] = I18N.get('export_spu_name', false);
  //     // 别名
  //     dtRemark[I18N.get('common_alias', false)] = I18N.get('export_spu_alias', false);
  //     // 商家药品编码
  //     dtRemark[I18N.get('med_medicine_code', false)] = I18N.get('export_tcm_medicine_code', false);
  //     if (orderType === EnumOrderType.药品订单) {
  //       // 供货价
  //       dtRemark[I18N.get('med_buy_price', false)] = I18N.get('export_buy_price_2', false);
  //     } else if (orderType === EnumOrderType.处方订单) {
  //       // 供货价
  //       dtRemark[I18N.get('med_buy_price', false)] = I18N.get('export_buy_price', false);
  //     }
  //     // 底价
  //     dtRemark[I18N.get('med_preserve_price', false)] = I18N.get('export_floor_price', false);
  //     // 药品名称
  //     dtRemark[I18N.get('med_agent_name', false)] = I18N.get('export_tcm_name', false);
  //     // 药品简称
  //     dtRemark[I18N.get('med_short_name', false)] = I18N.get('export_tcm_name', false);
  //     if (orderType === EnumOrderType.药品订单) {
  //       // 批发单位
  //       dtRemark[I18N.get('med_wholesale_unit', false)] = I18N.get('export_tcm_wholesale_unit', false);
  //       // 零售单位
  //       dtRemark[I18N.get('med_retail_unit', false)] = I18N.get('export_tcm_retail_2_unit', false);
  //       // 装量
  //       dtRemark[I18N.get('med_specification', false)] = I18N.get('export_tcm_2_specification', false);
  //     } else if (orderType === EnumOrderType.处方订单) {
  //       // 零售单位
  //       dtRemark[I18N.get('med_retail_unit', false)] = I18N.get('export_tcm_retail_unit', false);
  //       // 装量
  //       dtRemark[I18N.get('med_specification', false)] = I18N.get('export_tcm_specification', false);
  //     }
  //     // 药效当量
  //     dtRemark[I18N.get('med_equivalent', false)] = I18N.get('export_tcm_equivalent', false);
  //     // 产地
  //     dtRemark[I18N.get('med_origin_place', false)] = I18N.get('export_optional', false);
  //     // 生产商
  //     dtRemark[I18N.get('med_manufacturer', false)] = I18N.get('export_optional', false);
  //     if (orderType === EnumOrderType.药品订单) {
  //       // 起订量
  //       dtRemark[I18N.get('order_min_purchase_amount', false)] = I18N.get('export_order_quantity', false);
  //       // 订购单位
  //       dtRemark[I18N.get('order_purchase_unit', false)] = I18N.get('export_order_unit', false);
  //       // 新增库存
  //       dtRemark[I18N.get('common_add_storage', false)] = I18N.get('export_add_storage', false);
  //       // 库存
  //       dtRemark[I18N.get('common_storage', false)] = I18N.get('export_2_storage', false);
  //     } else if (orderType === EnumOrderType.处方订单) {
  //       // 新增库存
  //       dtRemark[I18N.get('common_add_storage', false)] = I18N.get('export_add_storage', false);
  //       // 库存
  //       dtRemark[I18N.get('common_storage', false)] = I18N.get('export_storage', false);
  //     }

  //     dataList.push(dtRemark);

  //     for (const obj of mallMedicineList as IMallTCMDetail[]) {
  //       const dt: IExcelColumnName = {};
  //       // 脉景品名
  //       dt[I18N.get('med_spu_name', false)] = obj.spu.name;
  //       // 别名
  //       dt[I18N.get('common_alias', false)] = obj.spu.alias || '';
  //       // 商家药品编码
  //       dt[I18N.get('med_medicine_code', false)] = obj.medicine_code || '';
  //       // 供货价
  //       dt[I18N.get('med_buy_price', false)] = obj.supply_price || '';
  //       // 底价
  //       dt[I18N.get('med_preserve_price', false)] = obj.floor_price || '';
  //       // 药品名称
  //       dt[I18N.get('med_agent_name', false)] = obj.name || '';
  //       // 药品简称
  //       dt[I18N.get('med_short_name', false)] = obj.short_name || '';
  //       if (orderType === EnumOrderType.药品订单) {
  //         // 批发单位
  //         dt[I18N.get('med_wholesale_unit', false)] = obj.wholesale_unit || '';
  //       }
  //       // 零售单位
  //       dt[I18N.get('med_retail_unit', false)] = obj.retail_unit || '';
  //       // 装量
  //       dt[I18N.get('med_specification', false)] = obj.specification || '';
  //       // 药效当量
  //       dt[I18N.get('med_equivalent', false)] = obj.equivalent || '';
  //       // 产地
  //       dt[I18N.get('med_origin_place', false)] = obj.production_place || '';
  //       // 生产商
  //       dt[I18N.get('med_manufacturer', false)] = obj.manufacturer || '';
  //       if (orderType === EnumOrderType.药品订单) {
  //         // 起订量
  //         dt[I18N.get('order_min_purchase_amount', false)] = obj.min_order_quantity || '';
  //         // 订购单位
  //         dt[I18N.get('order_purchase_unit', false)] = obj.order_unit || '';
  //       }
  //       // 新增库存
  //       dt[I18N.get('common_add_storage', false)] = '';
  //       // 库存
  //       dt[I18N.get('common_storage', false)] = obj.storage || '0';

  //       dataList.push(dt);
  //     }
  //     let sheetName = I18N.get('med_chinese', false);
  //     if (orderType === EnumOrderType.药品订单) {
  //       sheetName += '-药品订单'
  //     } else if (orderType === EnumOrderType.处方订单) {
  //       sheetName += '-处方订单'
  //     }
  //     return {
  //       sheetName,
  //       data: dataList,
  //     };
  //   };

  //   // 脉景中药列表
  //   const basicSheet = (): IExcelSheet => {
  //     const basicList: IExcelColumnName[] = [];

  //     const dtRemark: IExcelColumnName = {};
  //     // 脉景品名
  //     dtRemark[I18N.get('med_spu_name', false)] = I18N.get('export_spu_name', false);
  //     // 别名
  //     dtRemark[I18N.get('common_alias', false)] = I18N.get('export_spu_alias', false);
  //     // 商家药品编码
  //     dtRemark[I18N.get('med_medicine_code', false)] = I18N.get('export_tcm_medicine_code', false);
  //     if (orderType === EnumOrderType.药品订单) {
  //       // 供货价
  //       dtRemark[I18N.get('med_buy_price', false)] = I18N.get('export_buy_price_2', false);
  //     } else if (orderType === EnumOrderType.处方订单) {
  //       // 供货价
  //       dtRemark[I18N.get('med_buy_price', false)] = I18N.get('export_buy_price', false);
  //     }
  //     // 底价
  //     dtRemark[I18N.get('med_preserve_price', false)] = I18N.get('export_floor_price', false);
  //     // 药品名称
  //     dtRemark[I18N.get('med_agent_name', false)] = I18N.get('export_tcm_name', false);
  //     // 药品简称
  //     dtRemark[I18N.get('med_short_name', false)] = I18N.get('export_tcm_name', false);
  //     if (orderType === EnumOrderType.药品订单) {
  //       // 批发单位
  //       dtRemark[I18N.get('med_wholesale_unit', false)] = I18N.get('export_tcm_wholesale_unit', false);
  //       // 零售单位
  //       dtRemark[I18N.get('med_retail_unit', false)] = I18N.get('export_tcm_retail_2_unit', false);
  //       // 装量
  //       dtRemark[I18N.get('med_specification', false)] = I18N.get('export_tcm_2_specification', false);
  //     } else if (orderType === EnumOrderType.处方订单) {
  //       // 零售单位
  //       dtRemark[I18N.get('med_retail_unit', false)] = I18N.get('export_tcm_retail_unit', false);
  //       // 装量
  //       dtRemark[I18N.get('med_specification', false)] = I18N.get('export_tcm_specification', false);
  //     }
  //     // 药效当量
  //     dtRemark[I18N.get('med_equivalent', false)] = I18N.get('export_tcm_equivalent', false);
  //     // 产地
  //     dtRemark[I18N.get('med_origin_place', false)] = I18N.get('export_optional', false);
  //     // 生产商
  //     dtRemark[I18N.get('med_manufacturer', false)] = I18N.get('export_optional', false);
  //     if (orderType === EnumOrderType.药品订单) {
  //       // 起订量
  //       dtRemark[I18N.get('order_min_purchase_amount', false)] = I18N.get('export_order_quantity', false);
  //       // 订购单位
  //       dtRemark[I18N.get('order_purchase_unit', false)] = I18N.get('export_order_unit', false);
  //       // 新增库存
  //       dtRemark[I18N.get('common_add_storage', false)] = I18N.get('export_add_storage', false);
  //       // 库存
  //       dtRemark[I18N.get('common_storage', false)] = I18N.get('export_2_storage', false);
  //     } else if (orderType === EnumOrderType.处方订单) {
  //       // 新增库存
  //       dtRemark[I18N.get('common_add_storage', false)] = I18N.get('export_add_storage', false);
  //       // 库存
  //       dtRemark[I18N.get('common_storage', false)] = I18N.get('export_storage', false);
  //     }
  //     basicList.push(dtRemark);

  //     for (const obj of spuMedicineList) {
  //       const dt: IExcelColumnName = {};
  //       // 脉景品名
  //       dt[I18N.get('med_spu_name', false)] = obj.name;
  //       // 别名
  //       dt[I18N.get('common_alias', false)] = obj.alias || '';
  //       // 商家药品编码
  //       dt[I18N.get('med_medicine_code', false)] = '';
  //       // 供货价
  //       dt[I18N.get('med_buy_price', false)] = '';
  //       // 底价
  //       dt[I18N.get('med_preserve_price', false)] = '';
  //       // 药品名称
  //       dt[I18N.get('med_agent_name', false)] = '';
  //       // 药品简称
  //       dt[I18N.get('med_short_name', false)] = '';
  //       if (orderType === EnumOrderType.药品订单) {
  //         // 批发单位
  //         dt[I18N.get('med_wholesale_unit', false)] = '';
  //       }
  //       // 零售单位
  //       dt[I18N.get('med_retail_unit', false)] = '';
  //       // 装量
  //       dt[I18N.get('med_specification', false)] = '';
  //       // 药效当量
  //       dt[I18N.get('med_equivalent', false)] = '';
  //       // 产地
  //       dt[I18N.get('med_origin_place', false)] = '';
  //       // 生产商
  //       dt[I18N.get('med_manufacturer', false)] = '';
  //       if (orderType === EnumOrderType.药品订单) {
  //         // 起订量
  //         dt[I18N.get('order_min_purchase_amount', false)] = '';
  //         // 订购单位
  //         dt[I18N.get('order_purchase_unit', false)] = '';
  //       }
  //       // 新增库存
  //       dt[I18N.get('common_add_storage', false)] = '';
  //       // 库存
  //       dt[I18N.get('common_storage', false)] = obj.storage || '0';

  //       basicList.push(dt);
  //     }
  //     return {
  //       sheetName: I18N.get('med_mc_list', false),
  //       data: basicList,
  //     };
  //   };

  //   const sheetList: IExcelSheet[] = [];
  //   sheetList.push(tcmSheet());
  //   sheetList.push(basicSheet());

  //   const excelData: IExcelFile = {
  //     fileName: `${fileName || ''}_${UtilDateTime.nowFormat('yyyyMMddhhmmss')}.xlsx`,
  //     sheetList,
  //   };
  //   this.saveExcel(excelData);
  // };

  /**
   * 中成药/中药导出
   *
   * @memberof FileService
   */
  // mallCpmExport = (fileName: string, file: Blob,): void => {
    mallGroupMedicineExport = (fileName: string, file: Blob,): void => {
    const blob = new Blob([file], { type: 'application/octet-stream' });
    const downLoadUrl = window.URL.createObjectURL(blob);
    if (!!downLoadUrl) {
      const downLoadLink = document.createElement('a');
      downLoadLink.download = `${fileName || ''}_${UtilDateTime.nowFormat('yyyyMMddhhmmss')}.xlsx`;
      downLoadLink.href = downLoadUrl;
      downLoadLink.style.display = 'none';
      // 兼容：某些浏览器不支持HTML5的download属性
      if (typeof downLoadLink.download === 'undefined') {
        downLoadLink.setAttribute('target', '_blank');
      }
      // 触发点击
      document.body.appendChild(downLoadLink);
      downLoadLink.click();
      // 然后移除
      document.body.removeChild(downLoadLink);
      // 释放blob URL地址
      window.URL.revokeObjectURL(downLoadUrl);
    }
    // const cpmSheet = (): IExcelSheet => {
    //   const dataList: IExcelColumnName[] = [];

    //   const dtRemark: IExcelColumnName = {};
    //   // 脉景品名
    //   dtRemark[I18N.get('med_spu_name', false)] = I18N.get('export_spu_name', false);
    //   // 别名
    //   dtRemark[I18N.get('common_alias', false)] = I18N.get('export_spu_alias', false);
    //   // 商家药品编码
    //   dtRemark[I18N.get('med_medicine_code', false)] = I18N.get('export_cpm_medicine_code', false);
    //   if (orderType === EnumOrderType.处方订单) {
    //     // 供货价
    //     dtRemark[I18N.get('med_buy_price', false)] = I18N.get('export_buy_price', false);
    //   } else if (orderType === EnumOrderType.药品订单) {
    //     // 供货价
    //     dtRemark[I18N.get('med_buy_price', false)] = I18N.get('export_buy_price_2', false);
    //   }
    //   // 底价
    //   dtRemark[I18N.get('med_preserve_price', false)] = I18N.get('export_floor_price', false);
    //   // 药品名称
    //   dtRemark[I18N.get('med_agent_name', false)] = I18N.get('export_required', false);
    //   // 药品简称
    //   dtRemark[I18N.get('med_short_name', false)] = I18N.get('export_required', false);
    //   // 简称拼音
    //   dtRemark[I18N.get('med_agent', false) + I18N.get('med_short_name_pinyin2', false)] = I18N.get('export_required', false);
    //   if (orderType === EnumOrderType.处方订单) {
    //     // 零售单位
    //     dtRemark[I18N.get('med_retail_unit', false)] = I18N.get('export_cpm_unit', false);
    //     // 装量
    //     dtRemark[I18N.get('med_specification', false)] = I18N.get('export_cpm_specification', false);
    //   } else if (orderType === EnumOrderType.药品订单) {
    //     // 批发单位
    //     dtRemark[I18N.get('med_wholesale_unit', false)] = I18N.get('export_cpm_wholesale_unit', false);
    //     // 零售单位
    //     dtRemark[I18N.get('med_retail_unit', false)] = I18N.get('export_cpm_unit_2', false);
    //     // 装量
    //     dtRemark[I18N.get('med_specification', false)] = I18N.get('export_cpm_specification_2', false);
    //   }
    //   // 规格(药效当量)
    //   dtRemark[I18N.get('med_cpm_equivalent', false)] = I18N.get('export_cpm_equivalent', false);
    //   // 单次用量
    //   dtRemark[I18N.get('med_single_dose', false)] = I18N.get('export_required', false);
    //   // 每日服用次数
    //   dtRemark[I18N.get('med_daily_take_times', false)] = I18N.get('export_required', false);
    //   // 生产商
    //   dtRemark[I18N.get('med_manufacturer', false)] = I18N.get('export_required', false);
    //   // 产地
    //   dtRemark[I18N.get('med_origin_place', false)] = I18N.get('export_optional', false);
    //   if (orderType === EnumOrderType.药品订单) {
    //     // 起订量
    //     dtRemark[I18N.get('order_min_purchase_amount', false)] = I18N.get('export_order_quantity', false);
    //     // 订购单位
    //     dtRemark[I18N.get('order_purchase_unit', false)] = I18N.get('export_order_unit', false);
    //   }
    //   // 介绍
    //   dtRemark[I18N.get('med_agent', false) + I18N.get('med_cpm_introduce', false)] = I18N.get('export_cpm_introduce', false);
    //   // 新增库存
    //   dtRemark[I18N.get('common_add_storage', false)] = I18N.get('export_add_storage', false);
    //   if (orderType === EnumOrderType.处方订单) {
    //     // 库存
    //     dtRemark[I18N.get('common_storage', false)] = I18N.get('export_storage', false);
    //   } else if (orderType === EnumOrderType.药品订单) {
    //     // 库存
    //     dtRemark[I18N.get('common_storage', false)] = I18N.get('export_2_storage', false);
    //   }

    //   dataList.push(dtRemark);

    //   for (const obj of mallMedicineList as IMallCPMDetail[]) {
    //     const dt: IExcelColumnName = {};
    //     // 脉景品名
    //     dt[I18N.get('med_spu_name', false)] = obj.spu.name;
    //     // 别名
    //     dt[I18N.get('common_alias', false)] = obj.spu.alias || '';
    //     // 商家药品编码
    //     dt[I18N.get('med_medicine_code', false)] = obj.medicine_code || '';
    //     // 供货价
    //     dt[I18N.get('med_buy_price', false)] = obj.supply_price || '';
    //     // 底价
    //     dt[I18N.get('med_preserve_price', false)] = obj.floor_price || '';
    //     // 药品名称
    //     dt[I18N.get('med_agent_name', false)] = obj.name || '';
    //     // 药品简称
    //     dt[I18N.get('med_short_name', false)] = obj.short_name || '';
    //     // 简称拼音
    //     dt[I18N.get('med_agent', false) + I18N.get('med_short_name_pinyin2', false)] = obj.pinyin || '';
    //     if (orderType === EnumOrderType.药品订单) {
    //       // 批发单位
    //       dt[I18N.get('med_wholesale_unit', false)] = obj.wholesale_unit || '';
    //     }
    //     // 零售单位
    //     dt[I18N.get('med_retail_unit', false)] = obj.retail_unit || '';
    //     // 装量
    //     dt[I18N.get('med_specification', false)] = obj.specification || '';
    //     // 规格(药效当量)
    //     dt[I18N.get('med_cpm_equivalent', false)] = obj.equivalent || '';
    //     // 单次用量
    //     dt[I18N.get('med_single_dose', false)] = obj.single_dose || '';
    //     // 每日服用次数
    //     dt[I18N.get('med_daily_take_times', false)] = obj.daily_take_times || '';
    //     // 生产商
    //     dt[I18N.get('med_manufacturer', false)] = obj.manufacturer || '';
    //     // 产地
    //     dt[I18N.get('med_origin_place', false)] = obj.production_place || '';
    //     if (orderType === EnumOrderType.药品订单) {
    //       // 起订量
    //       dt[I18N.get('order_min_purchase_amount', false)] = obj.min_order_quantity || '';
    //       // 订购单位
    //       dt[I18N.get('order_purchase_unit', false)] = obj.order_unit || '';
    //     }
    //     // 介绍
    //     dt[I18N.get('med_agent', false) + I18N.get('med_cpm_introduce', false)] = obj.description || '';
    //     // 新增库存
    //     dt[I18N.get('common_add_storage', false)] = '';
    //     // 库存
    //     dt[I18N.get('common_storage', false)] = obj.storage || '0';

    //     dataList.push(dt);
    //   }

    //   let sheetName = I18N.get('med_type_zcy', false);
    //   if (orderType === EnumOrderType.药品订单) {
    //     sheetName += '-药品订单'
    //   } else if (orderType === EnumOrderType.处方订单) {
    //     sheetName += '-处方订单'
    //   }
    //   return {
    //     sheetName,
    //     data: dataList,
    //   };
    // };

    // const basicSheet = (): IExcelSheet => {
    //   const basicList: IExcelColumnName[] = [];

    //   const dtRemark: IExcelColumnName = {};
    //   // 脉景品名
    //   dtRemark[I18N.get('med_spu_name', false)] = I18N.get('export_spu_name', false);
    //   // 别名
    //   dtRemark[I18N.get('common_alias', false)] = I18N.get('export_spu_alias', false);
    //   // 商家药品编码
    //   dtRemark[I18N.get('med_medicine_code', false)] = I18N.get('export_cpm_medicine_code', false);
    //   if (orderType === EnumOrderType.处方订单) {
    //     // 供货价
    //     dtRemark[I18N.get('med_buy_price', false)] = I18N.get('export_buy_price', false);
    //   } else if (orderType === EnumOrderType.药品订单) {
    //     // 供货价
    //     dtRemark[I18N.get('med_buy_price', false)] = I18N.get('export_buy_price_2', false);
    //   }
    //   // 底价
    //   dtRemark[I18N.get('med_preserve_price', false)] = I18N.get('export_floor_price', false);
    //   // 药品名称
    //   dtRemark[I18N.get('med_agent_name', false)] = I18N.get('export_required', false);
    //   // 药品简称
    //   dtRemark[I18N.get('med_short_name', false)] = I18N.get('export_required', false);
    //   // 简称拼音
    //   dtRemark[I18N.get('med_agent', false) + I18N.get('med_short_name_pinyin2', false)] = I18N.get('export_required', false);
    //   if (orderType === EnumOrderType.处方订单) {
    //     // 零售单位
    //     dtRemark[I18N.get('med_retail_unit', false)] = I18N.get('export_cpm_unit', false);
    //     // 装量
    //     dtRemark[I18N.get('med_specification', false)] = I18N.get('export_cpm_specification', false);
    //   } else if (orderType === EnumOrderType.药品订单) {
    //     // 批发单位
    //     dtRemark[I18N.get('med_wholesale_unit', false)] = I18N.get('export_cpm_wholesale_unit', false);
    //     // 零售单位
    //     dtRemark[I18N.get('med_retail_unit', false)] = I18N.get('export_cpm_unit_2', false);
    //     // 装量
    //     dtRemark[I18N.get('med_specification', false)] = I18N.get('export_cpm_specification_2', false);
    //   }
    //   // 规格(药效当量)
    //   dtRemark[I18N.get('med_cpm_equivalent', false)] = I18N.get('export_cpm_equivalent', false);
    //   // 单次用量
    //   dtRemark[I18N.get('med_single_dose', false)] = I18N.get('export_required', false);
    //   // 每日服用次数
    //   dtRemark[I18N.get('med_daily_take_times', false)] = I18N.get('export_required', false);
    //   // 生产商
    //   dtRemark[I18N.get('med_manufacturer', false)] = I18N.get('export_required', false);
    //   // 产地
    //   dtRemark[I18N.get('med_origin_place', false)] = I18N.get('export_optional', false);
    //   if (orderType === EnumOrderType.药品订单) {
    //     // 起订量
    //     dtRemark[I18N.get('order_min_purchase_amount', false)] = I18N.get('export_order_quantity', false);
    //     // 订购单位
    //     dtRemark[I18N.get('order_purchase_unit', false)] = I18N.get('export_order_unit', false);
    //   }
    //   // 介绍
    //   dtRemark[I18N.get('med_agent', false) + I18N.get('med_cpm_introduce', false)] = I18N.get('export_cpm_introduce', false);
    //   // 新增库存
    //   dtRemark[I18N.get('common_add_storage', false)] = I18N.get('export_add_storage', false);
    //   if (orderType === EnumOrderType.处方订单) {
    //     // 库存
    //     dtRemark[I18N.get('common_storage', false)] = I18N.get('export_storage', false);
    //   } else if (orderType === EnumOrderType.药品订单) {
    //     // 库存
    //     dtRemark[I18N.get('common_storage', false)] = I18N.get('export_2_storage', false);
    //   }

    //   basicList.push(dtRemark);

    //   for (const obj of spuMedicineList) {
    //     const dt: IExcelColumnName = {};
    //     // 脉景品名
    //     dt[I18N.get('med_spu_name', false)] = obj.name;
    //     // 别名
    //     dt[I18N.get('common_alias', false)] = obj.alias || '';
    //     // 商家药品编码
    //     dt[I18N.get('med_medicine_code', false)] = '';
    //     // 供货价
    //     dt[I18N.get('med_buy_price', false)] = '';
    //     // 底价
    //     dt[I18N.get('med_preserve_price', false)] = '';
    //     // 药品名称
    //     dt[I18N.get('med_agent_name', false)] = '';
    //     // 药品简称
    //     dt[I18N.get('med_short_name', false)] = '';
    //     // 简称拼音
    //     dt[I18N.get('med_agent', false) + I18N.get('med_short_name_pinyin2', false)] = '';
    //     if (orderType === EnumOrderType.药品订单) {
    //       // 批发单位
    //       dt[I18N.get('med_wholesale_unit', false)] = '';
    //     }
    //     // 零售单位
    //     dt[I18N.get('med_retail_unit', false)] = '';
    //     // 装量
    //     dt[I18N.get('med_specification', false)] = '';
    //     // 规格(药效当量)
    //     dt[I18N.get('med_cpm_equivalent', false)] = '';
    //     // 单次用量
    //     dt[I18N.get('med_single_dose', false)] = '';
    //     // 每日服用次数
    //     dt[I18N.get('med_daily_take_times', false)] = '';
    //     // 生产商
    //     dt[I18N.get('med_manufacturer', false)] = '';
    //     // 产地
    //     dt[I18N.get('med_origin_place', false)] = '';
    //     if (orderType === EnumOrderType.药品订单) {
    //       // 起订量
    //       dt[I18N.get('order_min_purchase_amount', false)] = '';
    //       // 订购单位
    //       dt[I18N.get('order_purchase_unit', false)] = '';
    //     }
    //     // 介绍
    //     dt[I18N.get('med_agent', false) + I18N.get('med_cpm_introduce', false)] = '';
    //     // 新增库存
    //     dt[I18N.get('common_add_storage', false)] = '';
    //     // 库存
    //     dt[I18N.get('common_storage', false)] = obj.storage || '0';

    //     basicList.push(dt);
    //   }
    //   return {
    //     sheetName: I18N.get('med_mc_cpm_list', false),
    //     data: basicList,
    //   };
    // };

    // const sheetList: IExcelSheet[] = [];
    // sheetList.push(cpmSheet());
    // sheetList.push(basicSheet());

    // const excelData: IExcelFile = {
    //   fileName: `${fileName || ''}_${UtilDateTime.nowFormat('yyyyMMddhhmmss')}.xlsx`,
    //   sheetList,
    // };
    // this.saveExcel(excelData);
  };

  /**
   * 保存excel文件
   * @memberof FileService
   */
  saveExcel = (excelFile: IExcelFile): void => {
    const wb: XLSX.WorkBook = { SheetNames: [], Sheets: {} };
    for (const sheet of excelFile.sheetList) {
      const sheetName = sheet.sheetName;
      const sheetData = sheet.data;
      const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(sheetData);
      wb.SheetNames.push(sheetName);
      wb.Sheets[sheetName] = ws;
    }
    XLSX.writeFile(wb, excelFile.fileName);
  };

  // saveExcel = (data: any[], fileName: string, sheetName: string): void => {
  //     const ws = XLSX.utils.json_to_sheet(data);
  //     const wb = XLSX.utils.book_new();
  //     XLSX.utils.book_append_sheet(wb, ws, sheetName);
  //     XLSX.writeFile(wb, fileName);
  // }
}

export default new FileService();



// WEBPACK FOOTER //
// ./src/services/FileService.ts